import React, { useEffect, useMemo, useState } from "react";
import {
  getAllMessagesContactForm,
  getUnreadMessagesCountContactForm,
  markMessageContactForm,
  markAllMessageContactForm,
  deleteMessageContactForm,
} from "../../../services/contactformServices";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/Modal/Modal";

/* ====== Helpers ====== */
const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const formatDateTimeFR = (d) => {
  try {
    return new Date(d).toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const StatusPill = ({ read }) => (
  <span
    className={[
      "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium",
      read ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
           : "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    ].join(" ")}
  >
    <span className={["w-1.5 h-1.5 rounded-full", read ? "bg-emerald-500" : "bg-rose-500"].join(" ")} />
    {read ? "Lu" : "Non lu"}
  </span>
);

/* ====== Modal de lecture ====== */
function MessageViewModal({ isOpen, onClose, message, onToggleRead, onDelete }) {
  if (!isOpen || !message) return null;

  const { id, name, email, message: body, created_at, read_message } = message;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      labelledById="contact-read-title"
      describedById="contact-read-desc"
      initialFocusSelector="#close-btn"
    >
      <ModalHeader
        id="contact-read-title"
        title="Message du formulaire de contact"
        subtitle={formatDateTimeFR(created_at)}
        onClose={onClose}
      />
      <ModalBody id="contact-read-desc">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Nom</div>
              <div className="font-medium">{name || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Email</div>
              {email ? (
                <a className="font-medium text-indigo-600 hover:underline" href={`mailto:${email}`}>
                  {email}
                </a>
              ) : (
                <div className="font-medium">—</div>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Message</div>
            <div className="whitespace-pre-wrap rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-200">
              {body || "—"}
            </div>
          </div>

          <div className="pt-1">
            <StatusPill read={!!read_message} />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          onClick={() => onToggleRead(id)}
          className="inline-flex items-center px-4 h-10 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
        >
          {read_message ? "Marquer comme non lu" : "Marquer comme lu"}
        </button>
        <button
          onClick={() => onDelete(id)}
          className="inline-flex items-center px-4 h-10 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
        >
          Supprimer
        </button>
        <button
          id="close-btn"
          onClick={onClose}
          className="inline-flex items-center px-4 h-10 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
        >
          Fermer
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ====== Panel principal ====== */
const PAGE_SIZE = 10;

const ContactMessagesPanel = () => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // filtres
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [onlyUnread, setOnlyUnread] = useState(false);

  // pagination
  const [page, setPage] = useState(1);

  // modal
  const [openMsg, setOpenMsg] = useState(null);

  const refreshCounts = async () => {
    try {
      const c = await getUnreadMessagesCountContactForm();
      setUnreadCount(Number(c || 0));
    } catch {
      // silencieux
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErr("Impossible de charger les messages.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([fetchMessages(), refreshCounts()]);
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    let rows = messages.slice();
    if (onlyUnread) rows = rows.filter((m) => !m.read_message);
    if (term) {
      rows = rows.filter((m) => {
        const hay = [m.name, m.email, m.message].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(term);
      });
    }
    return rows;
  }, [messages, debouncedSearch, onlyUnread]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // reset page quand filtres changent
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, onlyUnread]);

  const onToggleRead = async (id) => {
    try {
      await markMessageContactForm(id);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, read_message: !m.read_message } : m))
      );
      // met à jour compteur local
      setUnreadCount((c) => {
        const wasUnread = messages.find((m) => m.id === id)?.read_message === false;
        return wasUnread ? Math.max(0, c - 1) : c + 1;
      });
    } catch (e) {
      console.error(e);
      setErr("Erreur lors de la mise à jour du statut de lecture.");
      setTimeout(() => setErr(""), 3000);
    }
  };

  const onMarkAllRead = async () => {
    try {
      await markAllMessageContactForm();
      setMessages((prev) => prev.map((m) => ({ ...m, read_message: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error(e);
      setErr("Erreur lors du marquage de tous les messages comme lus.");
      setTimeout(() => setErr(""), 3000);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement ce message ?")) return;
    try {
      await deleteMessageContactForm(id);
      const wasUnread = messages.find((m) => m.id === id)?.read_message === false;
      setMessages((prev) => prev.filter((m) => m.id !== id));
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
      if (openMsg?.id === id) setOpenMsg(null);
    } catch (e) {
      console.error(e);
      setErr("Erreur lors de la suppression du message.");
      setTimeout(() => setErr(""), 3000);
    }
  };

  return (
    <section className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 p-4 md:p-5">
      {/* En-tête & actions */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-semibold">Messages de contact</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Total : <span className="font-semibold">{messages.length}</span>{" "}
            — Non lus : <span className="font-semibold">{unreadCount}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={refreshAll}
            className="px-3 h-10 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
            title="Rafraîchir"
          >
            Rafraîchir
          </button>
          <button
            onClick={onMarkAllRead}
            className="px-3 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={unreadCount === 0}
          >
            Tout marquer comme lu
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="grid gap-3 md:grid-cols-[1fr,auto] items-center mt-4">
        <input
          type="text"
          placeholder="Rechercher (nom, email, texte du message)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-slate-700"
        />
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          Afficher uniquement les non lus
        </label>
      </div>

      {/* Bannières */}
      {err && (
        <div className="mt-3 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      {/* Tableau */}
        <div className="relative max-h-[70vh] overflow-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 mt-2">
          <table className="min-w-full bg-white dark:bg-slate-900">
            <thead className="sticky top-0 z-20 bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">Expéditeur</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">Aperçu</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center">Chargement…</td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  Aucun message{onlyUnread ? " non lu" : ""}.
                </td>
              </tr>
            ) : (
              pageItems.map((m) => (
                <tr
                  key={m.id}
                  className={m.read_message ? "bg-white dark:bg-slate-900" : "bg-indigo-50/40 dark:bg-indigo-950/10"}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDateTimeFR(m.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{m.name || "—"}</div>
                    {m.email ? (
                      <a className="text-xs text-indigo-600 hover:underline" href={`mailto:${m.email}`}>
                        {m.email}
                      </a>
                    ) : (
                      <div className="text-xs text-slate-400">—</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div
                      className="max-w-[520px] truncate"
                      title={m.message || ""}
                    >
                      {m.message || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <StatusPill read={!!m.read_message} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => setOpenMsg(m)}
                        className="px-3 h-9 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
                      >
                        Lire
                      </button>
                      <button
                        onClick={() => onToggleRead(m.id)}
                        className="px-3 h-9 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        {m.read_message ? "Non lu" : "Lu"}
                      </button>
                      <button
                        onClick={() => onDelete(m.id)}
                        className="px-3 h-9 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <div>
            Page {page} / {totalPages} — {filtered.length} message{filtered.length > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 h-9 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
              disabled={page === 1}
            >
              Précédent
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 h-9 rounded-lg ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-600 dark:hover:bg-slate-800"
              disabled={page === totalPages}
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Modal lecture */}
      <MessageViewModal
        isOpen={!!openMsg}
        onClose={() => setOpenMsg(null)}
        message={openMsg}
        onToggleRead={onToggleRead}
        onDelete={onDelete}
      />
    </section>
  );
};

export default ContactMessagesPanel;
